/**
 * SSO Type Definitions
 *
 * TypeScript types for SSO (Single Sign-On) operations.
 * Covers SSO provider configuration, authorization, and callback flows.
 */

import type { UserSanitized } from './user.types';
import type { TenantSanitized } from './tenant.types';

// ============================================================================
// ENUMS
// ============================================================================

/**
 * SSO provider types
 */
export const SSOProviderType = {
  MICROSOFT_ENTRA: 'MICROSOFT_ENTRA',
  GOOGLE: 'GOOGLE',
} as const;

export type SSOProviderType = typeof SSOProviderType[keyof typeof SSOProviderType];

/**
 * SSO provider status
 */
export const SSOProviderStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  PENDING_VERIFICATION: 'PENDING_VERIFICATION',
} as const;

export type SSOProviderStatus = typeof SSOProviderStatus[keyof typeof SSOProviderStatus];

// ============================================================================
// PUBLIC SSO TYPES (no auth required)
// ============================================================================

/**
 * Public SSO provider info returned for login page
 */
export interface SSOProviderPublic {
  providerId: string;
  providerType: SSOProviderType;
  displayName: string;
  status: SSOProviderStatus;
}

/**
 * SSO tenant settings (public)
 */
export interface SSOTenantInfo {
  tenantName: string;
  enforceSSO: boolean;
  providers: SSOProviderPublic[];
}

// ============================================================================
// SSO AUTHORIZATION TYPES
// ============================================================================

/**
 * SSO authorize request
 */
export interface SSOAuthorizeRequest {
  tenantCode: string;
  providerType?: string;
  providerId?: string;
  redirectUri: string;
}

/**
 * SSO authorize response
 */
export interface SSOAuthorizeResponse {
  authorizationUrl: string;
  state: string;
}

// ============================================================================
// SSO CALLBACK TYPES
// ============================================================================

/**
 * SSO callback request
 */
export interface SSOCallbackRequest {
  code: string;
  state: string;
  iss?: string;
}

/**
 * SSO callback response (same shape as login response)
 */
export interface SSOCallbackResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  refreshExpiresIn: number;
  user: UserSanitized;
  tenant: TenantSanitized;
}

// ============================================================================
// SSO PROVIDER CONFIGURATION TYPES (tenant admin)
// ============================================================================

/**
 * Full SSO provider configuration (admin view)
 */
export interface SSOProviderConfig {
  providerId: string;
  tenantId: string;
  providerType: SSOProviderType;
  displayName?: string;
  status: SSOProviderStatus;

  // Provider-specific configuration
  clientId: string;
  // clientSecret is never returned for security
  entraDirectoryId?: string; // Microsoft Entra only (tenant ID)
  hostedDomain?: string; // Google only

  // Settings
  allowedDomains?: string[];
  autoProvisionUsers: boolean;
  defaultRoles?: string[];
  enforceSSO?: boolean;

  // Statistics
  userCount?: number;

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

/**
 * DTO for creating an SSO provider
 */
export interface SSOProviderCreateDTO {
  providerType: SSOProviderType;
  displayName?: string;
  clientId: string;
  clientSecret: string;
  entraDirectoryId?: string;
  hostedDomain?: string;
  allowedDomains?: string[];
  autoProvisionUsers?: boolean;
  defaultRoles?: string[];
  enforceSSO?: boolean;
}

/**
 * DTO for updating an SSO provider
 */
export interface SSOProviderUpdateDTO {
  displayName?: string;
  clientId?: string;
  clientSecret?: string;
  entraDirectoryId?: string;
  hostedDomain?: string;
  allowedDomains?: string[];
  autoProvisionUsers?: boolean;
  defaultRoles?: string[];
  enforceSSO?: boolean;
  status?: SSOProviderStatus;
}

/**
 * SSO provider test connection response
 */
export interface SSOTestConnectionResponse {
  success: boolean;
  message: string;
  details?: {
    issuer?: string;
    authorizationEndpoint?: string;
    tokenEndpoint?: string;
  };
}

// ============================================================================
// SSO SETTINGS TYPES
// ============================================================================

/**
 * SSO enforcement settings for tenant
 */
export interface SSOEnforcementSettings {
  enforceSSO: boolean;
  autoProvisionUsers: boolean;
  defaultRoles?: string[];
  allowedDomains?: string[];
}
